/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { AxiosError } from 'axios';
import { createBaseErrorHandler } from 'src/core/utils/interceptors/errorHandlers/createBaseErrorHandler';

/**
 * Represents a generic error handler that gets called if all other
 * specific error handlers could not parse the error.
 */
export const createGenericErrorHandler = () => {
    const baseActions = createBaseErrorHandler();

    return {
        ...baseActions,

        /**
         * Handles the error by providing a default message.
         *
         * @param error The error that was thrown.
         * @returns A formatted Error object.
         */
        handle: async (error: AxiosError) => {
            const errorObject = new Error();
            errorObject.name = `${error.response?.status ?? 'Unknown'}`;
            if (typeof error.response?.data === 'string') {
                errorObject.message = error.response.data;
            } else if (
                error.response?.data &&
                typeof error.response?.data === 'object' &&
                'message' in error.response.data &&
                typeof error.response?.data.message === 'string'
            ) {
                errorObject.message = error.response.data.message;
            } else {
                errorObject.message = 'A server error occurred. Please refresh the page and try again.';
            }

            return errorObject;
        }
    };
};
